"use client";

import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { showToast } from "@/lib/toast";
import { roleService } from "@/services/roleService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface ModalDetailPermissionProps {
  isOpen: boolean;
  onClose: () => void;
  permissionData: any;
  onComplete: () => void;
}

// Updated role structure based on new PermissionSubject enum
const defaultRole = {
  user: { read: false, create: false, delete: false, update: false },
  role: { read: false, create: false, delete: false, update: false },
  information: { read: false, create: false, delete: false, update: false },
  category: { read: false, create: false, delete: false, update: false },
  article: { read: false, create: false, delete: false, update: false },
  image: { read: false, create: false, delete: false, update: false },
  page: { read: false, create: false, delete: false, update: false },
  blog_post: { read: false, create: false, delete: false, update: false },
  menu: { read: false, create: false, delete: false, update: false },
  film_list: { read: false, create: false, delete: false, update: false },
  author: { read: false, create: false, delete: false, update: false },
  robot_txt: { read: false, create: false, delete: false, update: false },
  widget: { read: false, create: false, delete: false, update: false },
  comment: { read: false, create: false, delete: false, update: false },
  log: { read: false, create: false, delete: false, update: false },
};

// Title mapping for roles
const titleRole = {
  user: "Người dùng",
  role: "Quyền hạn",
  category: "Danh mục",
  information: "Thông tin",
  article: "Bài viết",
  image: "Hình ảnh",
  page: "Trang",
  blog_post: "Blog Post",
  menu: "Menu",
  film_list: "Danh sách phim",
  author: "Tác giả",
  robot_txt: "Robot.txt",
  widget: "Widget",
  comment: "Bình luận",
  log: "Log",
};

function ModalDetailPermission({
  isOpen,
  onClose,
  permissionData,
}: ModalDetailPermissionProps) {
  const [roles, setRoles] = useState(JSON.parse(JSON.stringify(defaultRole)));
  const [isLoading, setIsLoading] = useState(false);

  const handleGetDetail = async () => {
    if (!permissionData?._id || !isOpen) return;
    try {
      setIsLoading(true);

      const res: any = await roleService.getPermissionsByRoleId(
        permissionData._id
      );

      const newRoles = JSON.parse(JSON.stringify(defaultRole));
      const data = res?.data?.data?.data;
      console.log("data: ", data);

      // Kiểm tra xem có phải admin không (subject: "all", action: ["manage"])
      const isAdmin = data.some(
        (permission: any) =>
          permission.subject === "all" && permission.action?.includes("manage")
      );

      if (isAdmin) {
        // Nếu là admin, set tất cả permissions = true
        Object.keys(newRoles).forEach((subject) => {
          newRoles[subject as keyof typeof newRoles] = {
            read: true,
            create: true,
            delete: true,
            update: true,
          };
        });
      } else {
        // Xử lý permissions bình thường
        for (let i = 0; i < data.length; i++) {
          const { subject, action } = data[i];

          // Bỏ qua các subject không có trong defaultRole
          if (!newRoles[subject as keyof typeof newRoles]) {
            continue;
          }

          // Nếu action là mảng rỗng, bỏ qua
          if (!action || action.length === 0) {
            continue;
          }

          // Xử lý từng action trong mảng
          action.forEach((act: string) => {
            // Kiểm tra action có tồn tại trong role structure không
            if (
              (newRoles[subject as keyof typeof newRoles] as any)[act] !==
              undefined
            ) {
              (newRoles[subject as keyof typeof newRoles] as any)[act] = true;
            }
          });
        }
      }

      setRoles(newRoles);
    } catch (error) {
      showToast.error(
        "Lỗi",
        "Không thể lấy chi tiết quyền hạn, vui lòng thử lại sau!"
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      handleGetDetail();
    }
  }, [isOpen]);

  const handleOnComplete = () => {
    if (isLoading) return;
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white uppercase">
            Chi tiết phân quyền
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col h-[80vh] overflow-y-auto">
          <div className="flex-1 overflow-y-auto max-h-[90%] p-6">
            <div className="flex flex-col gap-6">
              <div className="px-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-gray-900 dark:text-white">
                    Tên Vai trò
                  </label>
                  <input
                    disabled={true}
                    name="role-name"
                    value={permissionData?.name || ""}
                    type="text"
                    placeholder="Tên vai trò..."
                    className="w-full rounded-lg px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none"
                  />
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="w-full flex flex-row justify-between items-center px-2 py-3">
                  <p className="font-bold text-gray-900 truncate w-[40%]">
                    Tên phân quyền
                  </p>
                  <div className="flex flex-row justify-between items-center w-[60%]">
                    <div className="text-sm w-[20%] flex justify-center items-center font-bold text-gray-900">
                      Xem
                    </div>
                    <div className="text-sm w-[20%] flex justify-center items-center font-bold text-gray-900">
                      Thêm
                    </div>
                    <div className="text-sm w-[20%] flex justify-center items-center font-bold text-gray-900">
                      Xóa
                    </div>
                    <div className="text-sm w-[20%] flex justify-center items-center font-bold text-gray-900">
                      Sửa
                    </div>
                    <div className="text-sm w-[20%] flex justify-center items-center font-bold text-gray-900">
                      Tất cả
                    </div>
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Đang tải...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.keys(roles).map((role, index) => {
                    return (
                      <div
                        key={index}
                        className="bg-white border border-gray-200 rounded-lg p-4"
                      >
                        <div className="w-full flex flex-row justify-between items-center">
                          <p className="font-medium text-gray-900 truncate w-[40%] text-sm">
                            {titleRole[role as keyof typeof titleRole]}
                          </p>
                          <div className="flex flex-row justify-between items-center w-[60%]">
                            <div className="text-sm w-[20%] flex justify-center items-center">
                              {roles[role as keyof typeof roles].read ? (
                                <Check size={20} className="text-green-600" />
                              ) : (
                                <X size={20} className="text-red-600" />
                              )}
                            </div>
                            <div className="text-sm w-[20%] flex justify-center items-center">
                              {roles[role as keyof typeof roles].create ? (
                                <Check size={20} className="text-green-600" />
                              ) : (
                                <X size={20} className="text-red-600" />
                              )}
                            </div>
                            <div className="text-sm w-[20%] flex justify-center items-center">
                              {roles[role as keyof typeof roles].delete ? (
                                <Check size={20} className="text-green-600" />
                              ) : (
                                <X size={20} className="text-red-600" />
                              )}
                            </div>
                            <div className="text-sm w-[20%] flex justify-center items-center">
                              {roles[role as keyof typeof roles].update ? (
                                <Check size={20} className="text-green-600" />
                              ) : (
                                <X size={20} className="text-red-600" />
                              )}
                            </div>
                            <div className="text-sm w-[20%] flex justify-center items-center">
                              {roles[role as keyof typeof roles].read &&
                              roles[role as keyof typeof roles].create &&
                              roles[role as keyof typeof roles].delete &&
                              roles[role as keyof typeof roles].update ? (
                                <Check size={20} className="text-green-600" />
                              ) : (
                                <X size={20} className="text-red-600" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleOnComplete}
              disabled={isLoading}
            >
              Đóng
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ModalDetailPermission;
